import React, { useState } from "react";
import { usePathname } from '#app/lib/hooks/router-compat';
import {
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ErrorBoundary,
  Icon,
  Text,
} from "@salestrak/ui";

import { reportRenderError } from "#app/lib/error-reporter";

interface FallbackProps {
  error: Error;
  reset: () => void;
}

/**
 * App-wide boundary used at the root layout. Renders a full-bleed fallback
 * when the entire tree throws. Resets automatically when the route changes
 * so users aren't trapped by a sticky error on navigation.
 */
export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  return (
    <ErrorBoundary
      resetKey={pathname}
      onError={reportRenderError}
      fallback={(p) => <FullScreenErrorFallback {...p} />}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Adapter for the router's `defaultErrorComponent` (wired in `src/main.tsx`),
 * which supplies `{ error, retry }`. Renders the inline fallback so a failing
 * screen doesn't collapse the whole shell.
 */
export function RouteErrorBoundary({
  error,
  retry,
}: {
  error: Error;
  retry: () => void;
}) {
  return <InlineErrorFallback error={error} reset={retry} />;
}

// ---------------------------------------------------------------------------
// Fallback UIs
// ---------------------------------------------------------------------------

function FullScreenErrorFallback({ error, reset }: FallbackProps) {
  return (
    <Box
      fill="flex"
      background="background"
      align="center"
      justify="center"
      padding="lg"
      role="alert"
    >
      <Box direction="column" maxWidth={480} width="100%">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icon name="triangle-alert" size="xl" color="destructive" />
            </EmptyMedia>
            <EmptyTitle>Something went wrong</EmptyTitle>
            <EmptyDescription>
              {error.message ||
                "We hit an unexpected error. You can try again or reload the app."}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Box direction="row" gap="sm">
              <Button onClick={reset}>Try again</Button>
              <Button variant="outline" onClick={reloadApp}>
                Reload app
              </Button>
            </Box>
            {import.meta.env.DEV ? <ErrorDetails error={error} /> : null}
          </EmptyContent>
        </Empty>
      </Box>
    </Box>
  );
}

function InlineErrorFallback({ error, reset }: FallbackProps) {
  return (
    <Box fill="flex" padding="lg" align="center" justify="center" role="alert">
      <Box direction="column" maxWidth={480} width="100%">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Icon name="triangle-alert" size="lg" color="destructive" />
            </EmptyMedia>
            <EmptyTitle>This screen hit an error</EmptyTitle>
            <EmptyDescription>
              {error.message || "Something went wrong loading this view."}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={reset}>Try again</Button>
            {import.meta.env.DEV ? <ErrorDetails error={error} /> : null}
          </EmptyContent>
        </Empty>
      </Box>
    </Box>
  );
}

function ErrorDetails({ error }: { error: Error }) {
  const [open, setOpen] = useState(false);
  return (
    // `EmptyContent` already spaces its children (gap-4), so the old
    // `marginTop="md"` has nothing to compensate for.
    <Box gap="xs" width="100%">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger render={<Button variant="ghost" size="sm" />}>
          {open ? "Hide details" : "Show details"}
        </CollapsibleTrigger>
        <CollapsiblePanel>
          <Box
            surface="sunken"
            rounded="md"
            padding="sm"
            maxHeight={240}
            overflow="scroll"
          >
            <Text as="code" mono size="xs">
              {error.stack ?? error.message}
            </Text>
          </Box>
        </CollapsiblePanel>
      </Collapsible>
    </Box>
  );
}

function reloadApp() {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}
